import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { AiCombo } from "../../components/AiCombo";
import { AppDialog } from "../../components/AppDialog";

/**
 * Settings → Test Code → **Copy Test Group** — what Copy Test opens when the
 * record is a GROUP test, instead of going straight to the New form.
 *
 * Legacy shows this as a popup on `Testparameter_DetailView_CopyTest` (its
 * "Copy Test Group" layout group, `Model.xafml:69764`): two pickers, Matrix and
 * Test, then OK. The chosen test supplies the MEMBERS the new group test is
 * created with — `TestParameterViewControllercs.cs:1215` finds it with
 * `[MatrixName.MatrixName] = ? And [TestName] = ? And [IsGroup] = True`, and
 * `CopyFromOneTestgroupToAnother` (:1365) copies its `GroupTestMethod` rows.
 *
 * Only group tests are offered, for the same reason legacy filters on IsGroup:
 * a normal test has no members to give.
 */

type TestRow = { oid: string; test: string; matrix: string; isGroup?: string };
type Matrix = { id?: string; name: string };

export function TestCodeCopyGroupDialog({
  onClose, onPick,
}: {
  onClose: () => void;
  onPick: (src: { oid: string; matrix: string; test: string }) => void;
}) {
  const [rows, setRows] = useState<TestRow[]>([]);
  const [matrices, setMatrices] = useState<Matrix[]>([]);
  const [matrix, setMatrix] = useState("");
  const [test, setTest] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [list, mx] = await Promise.all([
          api.get("/api/v1/modules/set-testcode", { params: { pageSize: 1000 } }),
          api.get("/api/v1/test-methods/matrices"),
        ]);
        if (!alive) return;
        const data = list.data?.data?.rows ?? list.data?.rows ?? list.data?.data ?? [];
        setRows(Array.isArray(data) ? data : []);
        const mxd = mx.data?.data ?? mx.data ?? [];
        setMatrices(Array.isArray(mxd) ? mxd : []);
      } catch {
        // The shared axios handling already surfaced it; an empty picker says
        // the same thing without a second message.
        if (alive) { setRows([]); setMatrices([]); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  /** GROUP tests only — the list column is the display text 'Yes' / 'No'. */
  const groupTests = useMemo(
    () => rows.filter((r) => String(r.isGroup ?? "").toLowerCase() === "yes"),
    [rows],
  );

  const testsForMatrix = useMemo(() => {
    const seen = new Set<string>();
    return groupTests
      .filter((r) => !matrix || r.matrix === matrix)
      .filter((r) => (seen.has(r.test) ? false : (seen.add(r.test), true)));
  }, [groupTests, matrix]);

  const picked = testsForMatrix.find((r) => r.test === test);

  return (
    <AppDialog
      open
      title="Copy Group Test"
      width={720}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button
            className="ca-btn ca-btn-solid"
            disabled={!picked}
            onClick={() => picked && onPick({ oid: picked.oid, matrix, test })}
          >
            OK
          </button>
          <button className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="tc-copy-grid">
        <label className="tc-copy-lbl">Matrix<span className="req">*</span></label>
        <AiCombo
          value={matrix || null}
          options={matrices.map((m) => ({ value: m.name, label: m.name }))}
          placeholder={loading ? "Loading…" : "— Select —"}
          disabled={loading}
          onChange={(v) => { setMatrix(v ?? ""); setTest(""); }}
        />

        <label className="tc-copy-lbl">Test<span className="req">*</span></label>
        <AiCombo
          value={test || null}
          options={testsForMatrix.map((r) => ({ value: r.test, label: r.test }))}
          placeholder={loading ? "Loading…" : "— Select —"}
          disabled={loading}
          onChange={(v) => setTest(v ?? "")}
        />
      </div>
    </AppDialog>
  );
}
